/**
 * WebSocket wrapper with reconnection logic.
 * Exponential backoff: 100ms → 5s, max 10 attempts.
 */

import type { ServerToClientMessage, ClientToServerMessage, ClientAuthMessage } from './ServerProtocol'
import type { ConnectionState } from './ServerRoom'
import type { JoinTicketRequest } from '../shared'


export interface ServerWebSocketOptions {
  url: string
  /** Max reconnection attempts (default: 10) */
  maxReconnectAttempts?: number
  /** Initial reconnect delay in ms (default: 100) */
  initialReconnectDelay?: number
  /** Max reconnect delay in ms (default: 5000) */
  maxReconnectDelay?: number
  /** Auth message to send immediately on (re)connect */
  authMessage?: ClientAuthMessage
}

export interface ServerWebSocketEvents {
  onMessage?: (msg: ServerToClientMessage) => void
  onStateChange?: (state: ConnectionState) => void
  onError?: (error: Error) => void
}

export class ServerWebSocket {
  private ws: WebSocket | null = null
  private readonly url: string
  private readonly maxReconnectAttempts: number
  private readonly initialReconnectDelay: number
  private readonly maxReconnectDelay: number
  private reconnectAttempts = 0
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private disposed = false
  private events: ServerWebSocketEvents = {}
  private _state: ConnectionState = 'disconnected'
  private _getJoinTicket?: (req: JoinTicketRequest) => Promise<string>
  private _authMessage?: ClientAuthMessage
  /**
   * Original matchmaking intent for ticket re-minting on reconnect. The auth
   * frame deliberately does NOT carry criteria/createOptions, so they are kept
   * here and replayed into the reconnect JoinTicketRequest — a criteria-game
   * that drops into the createRoom fallthrough must re-matchmake with intent.
   */
  private _joinOptions?: Pick<JoinTicketRequest, 'criteria' | 'createOptions' | 'persistentKey'>
  private _isReconnect = false
  private _authenticated = false

  constructor(options: ServerWebSocketOptions) {
    this.url = options.url
    this.maxReconnectAttempts = options.maxReconnectAttempts ?? 10
    this.initialReconnectDelay = options.initialReconnectDelay ?? 100
    this.maxReconnectDelay = options.maxReconnectDelay ?? 5000
    this._authMessage = options.authMessage
  }

  get state(): ConnectionState {
    return this._state
  }

  /**
   * Set event handlers.
   */
  on(events: ServerWebSocketEvents): void {
    this.events = { ...this.events, ...events }
  }

  /**
   * Set the join ticket provider for reconnections.
   */
  setTicketProvider(fn: (req: JoinTicketRequest) => Promise<string>): void {
    this._getJoinTicket = fn
  }

  /**
   * Retain the original matchmaking criteria/create-options so the reconnect
   * ticket is re-minted with the same intent as the initial join.
   */
  setJoinOptions(opts: Pick<JoinTicketRequest, 'criteria' | 'createOptions' | 'persistentKey'>): void {
    this._joinOptions = opts
  }

  /**
   * Set the auth message to send on connect.
   */
  setAuthMessage(msg: ClientAuthMessage): void {
    this._authMessage = msg
  }

  /**
   * Retarget the room a reconnect resumes after a server-driven move
   * (move_player / Req 19). The socket itself is unchanged; this only updates
   * the `_roomId`/`roomCode` replayed in the reconnect auth frame.
   */
  retargetRoom(roomId: string, roomCode: string): void {
    if (!this._authMessage) return
    this._authMessage = { ...this._authMessage, _roomId: roomId, roomCode }
  }

  /**
   * Connect to the WebSocket server.
   * On reconnection, sends a fresh auth token with { reconnect: true }.
   */
  connect(): void {
    if (this.disposed) return
    this._authenticated = false
    this.setState(this._isReconnect ? 'reconnecting' : 'connecting')

    try {
      // Clean up previous WebSocket to prevent stale event handlers
      if (this.ws) {
        this.ws.onopen = null
        this.ws.onmessage = null
        this.ws.onclose = null
        this.ws.onerror = null
        this.ws = null
      }

      const socket = new WebSocket(this.url)
      this.ws = socket

      socket.onopen = () => this.onOpen(socket)
      socket.onmessage = (event) => this.onMessage(event)
      socket.onclose = (event) => this.onClose(event)
      socket.onerror = () => this.onError()
    } catch (err) {
      console.error(`[RUN:transport] connect() threw`, err)
      this.events.onError?.(err instanceof Error ? err : new Error('Failed to connect'))
      this.attemptReconnect()
    }
  }

  /**
   * Send a message to the server.
   */
  send(msg: ClientToServerMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN && this._authenticated) {
      this.ws.send(JSON.stringify(msg))
    }
  }

  /**
   * Reset the reconnect counter after the server confirms the connection is valid.
   */
  confirmConnection(): void {
    this.reconnectAttempts = 0
  }

  /**
   * Close the connection. Does not attempt reconnection.
   */
  close(): void {
    this.disposed = true
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    if (this.ws) {
      this.ws.close(1000, 'Client closed')
      this.ws = null
    }
    this.setState('disconnected')
  }

  private async onOpen(socket: WebSocket): Promise<void> {
    // On reconnect, get a fresh ticket and re-authenticate
    if (this._isReconnect && this._authMessage && this._getJoinTicket) {
      try {
        const req: JoinTicketRequest = {
          roomType: this._authMessage.roomType,
          roomCode: this._authMessage.roomCode,
          action: this._authMessage.action || 'joinOrCreate',
          criteria: this._joinOptions?.criteria,
          createOptions: this._joinOptions?.createOptions,
          persistentKey: this._joinOptions?.persistentKey,
          _roomId: this._authMessage._roomId,
          reconnect: true,
        }
        const freshTicket = await this._getJoinTicket(req)
        // Check that socket is still current and open after async work
        if (this.ws !== socket || socket.readyState !== WebSocket.OPEN) return
        const authMsg = { ...this._authMessage, ticket: freshTicket, reconnect: true }
        socket.send(JSON.stringify(authMsg))
        this._authenticated = true
      } catch {
        // A failed reconnect-ticket mint is transient (session-service hiccup,
        // GST not yet propagated): close and let the backoff loop redial. This
        // branch only runs on reconnect, and the max-attempts path is the only
        // terminal signal — emitting onError here would strand a session that
        // still has retry budget.
        console.error(`[RUN:transport] failed to get join ticket for reconnection`)
        socket.close()
        return
      }
    } else if (this._authMessage) {
      // Initial connect — send auth message as-is
      if (socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify(this._authMessage))
        this._authenticated = true
      }
    }
    // Verify socket is still current after any async work
    if (this.ws !== socket) return
    this.setState('connected')
  }

  private onMessage(event: MessageEvent): void {
    try {
      const msg = JSON.parse(event.data as string) as ServerToClientMessage
      this.events.onMessage?.(msg)
    } catch {
      console.warn(`[RUN:transport] failed to parse message`)
    }
  }

  private onClose(event: CloseEvent): void {
    console.warn(`[RUN:transport] onclose code=${event.code} reason=${event.reason} disposed=${this.disposed}`)
    this._authenticated = false
    if (this.disposed) {
      this.setState('disconnected')
      return
    }
    this.attemptReconnect()
  }

  private onError(): void {
    console.warn(`[RUN:transport] onerror; awaiting close before reconnecting`)
  }

  private attemptReconnect(): void {
    if (this.disposed) return
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error(`[RUN:transport] max reconnect attempts (${this.maxReconnectAttempts}) exceeded`)
      this._isReconnect = false
      this.setState('disconnected')
      this.events.onError?.(new Error('Max reconnection attempts exceeded'))
      return
    }

    this._isReconnect = true
    this.setState('reconnecting')
    this.reconnectAttempts++

    const delay = Math.min(
      this.initialReconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.maxReconnectDelay,
    )
    console.warn(`[RUN:transport] reconnecting attempt=${this.reconnectAttempts}/${this.maxReconnectAttempts} delay=${delay}ms`)

    this.reconnectTimer = setTimeout(() => {
      this.connect()
    }, delay)
  }

  private setState(state: ConnectionState): void {
    if (this._state !== state) {
      this._state = state
      this.events.onStateChange?.(state)
    }
  }
}
